// Settings feature screen (task 3.6) — Req 10.1–10.4.
//
// Business profile + rate card. Wired to the Phase 2 settings and ratecard
// handlers:
//   GET/PUT /settings                    business profile singleton
//   GET  /ratecard                       list rate card items
//   POST /ratecard                       add an item
//   PUT/DELETE /ratecard/{id}            edit / remove an item
//
// The business defaults to NOT VAT-registered (Req 10.4): the VAT-number field
// only shows when "VAT registered" is ticked, and the API clears any number while
// VAT is off. The rate card feeds AI quote drafting later (Req 10.2 / 4.4); here it
// is fully editable. QuickBooks connection status (Req 10.3) is wired via
// quickbooksSettingsSection() — connect/disconnect + status (task 5.4).

import { api } from '../api.js';
import {
  h, mount, runAsync,
  screenEl, card, sectionHeading, muted,
  field, checkboxField, makeInput, makeTextarea, makeSelect, makeCheckbox, errorSlot, setError,
  button, buttonRow, runAction, moneyFromPounds, toNum,
} from './ui.js';
import { quickbooksSettingsSection } from './quickbooks.js';

const RATE_CARD_KINDS = ['labour', 'material'];

export function settingsScreen() {
  const profileBody = h('div');
  const rateBody = h('div');

  loadProfile(profileBody);
  loadRateCard(rateBody);

  return screenEl('Settings', [
    sectionHeading('Business profile'),
    profileBody,
    sectionHeading('Rate card'),
    muted('Labour rates and common materials used when drafting quotes.'),
    rateBody,
    sectionHeading('QuickBooks'),
    quickbooksSettingsSection(),
  ], { section: 'settings' });
}

// --- Business profile ------------------------------------------------------

function loadProfile(container) {
  runAsync(
    container,
    () => api.get('/settings'),
    (settings) => renderProfileForm(settings, container),
    { loadingMessage: 'Loading settings…' },
  );
}

function renderProfileForm(settings, container) {
  const nameInput = makeInput({ value: settings.tradingName || '', placeholder: 'Trading name' });
  const addressInput = makeTextarea({ value: settings.address || '', rows: 3, placeholder: 'Business address' });
  const phoneInput = makeInput({ type: 'tel', value: (settings.contact && settings.contact.phone) || '', inputmode: 'tel' });
  const emailInput = makeInput({ type: 'email', value: (settings.contact && settings.contact.email) || '', inputmode: 'email' });
  const websiteInput = makeInput({ value: (settings.contact && settings.contact.website) || '', placeholder: 'https://' });
  const logoInput = makeInput({ value: settings.logoKey || '', placeholder: 'S3 object key (optional)' });
  const validityInput = makeInput({ type: 'number', value: settings.quoteValidityDays ?? 30, inputmode: 'numeric', min: '1', max: '365' });

  const vatCheckbox = makeCheckbox({ checked: !!settings.vatRegistered });
  const vatNumberInput = makeInput({ value: settings.vatNumber || '', placeholder: 'VAT registration number' });
  const vatNumberField = field('VAT number', vatNumberInput);
  const syncVatVisibility = () => { vatNumberField.style.display = vatCheckbox.checked ? '' : 'none'; };
  vatCheckbox.addEventListener('change', syncVatVisibility);
  syncVatVisibility();

  const err = errorSlot();
  const save = button('Save profile', { variant: 'primary' });
  save.addEventListener('click', async () => {
    const validity = parseInt(validityInput.value, 10);
    if (!Number.isFinite(validity) || validity < 1 || validity > 365) {
      return setError(err, 'Quote validity must be between 1 and 365 days.');
    }
    const payload = {
      tradingName: nameInput.value.trim(),
      address: addressInput.value.trim(),
      contact: { phone: phoneInput.value.trim(), email: emailInput.value.trim(), website: websiteInput.value.trim() },
      logoKey: logoInput.value.trim() || null,
      vatRegistered: vatCheckbox.checked,
      vatNumber: vatCheckbox.checked ? (vatNumberInput.value.trim() || null) : null,
      quoteValidityDays: validity,
    };
    const updated = await runAction(save, () => api.put('/settings', payload), { busyLabel: 'Saving…', errSlot: err });
    if (updated) loadProfile(container); // re-render with the persisted values
  });

  return h('div', { class: 'form' }, [
    field('Trading name', nameInput),
    field('Address', addressInput),
    field('Phone', phoneInput),
    field('Email', emailInput),
    field('Website', websiteInput),
    field('Logo key', logoInput, { hint: 'S3 key of an uploaded logo; used on PDFs later.' }),
    field('Quote validity (days)', validityInput),
    checkboxField('VAT registered', vatCheckbox, { hint: 'Off by default — no VAT is applied to quotes or invoices.' }),
    vatNumberField,
    err,
    buttonRow([save]),
  ]);
}

// --- Rate card -------------------------------------------------------------

function loadRateCard(container) {
  runAsync(
    container,
    () => api.get('/ratecard'),
    (res) => renderRateCard((res && res.items) || [], container),
    { loadingMessage: 'Loading rate card…' },
  );
}

function renderRateCard(items, container) {
  const reload = () => loadRateCard(container);

  const groups = RATE_CARD_KINDS.map((kind) => ({
    kind,
    items: items.filter((i) => i.kind === kind),
  }));

  const groupNodes = groups.map((g) =>
    card(
      g.items.length
        ? g.items.map((item) => rateCardRow(item, reload))
        : [muted(`No ${g.kind} items yet.`)],
      { title: g.kind === 'labour' ? 'Labour' : 'Materials' },
    ),
  );

  return h('div', {}, [...groupNodes, addItemForm(reload)]);
}

// A single rate card row with inline edit + delete.
function rateCardRow(item, reload) {
  const row = h('div', { class: 'rate-row' });

  const renderRead = () => {
    const editBtn = button('Edit', { small: true });
    const delBtn = button('Delete', { small: true });
    const err = errorSlot();
    editBtn.addEventListener('click', () => mount(row, renderEdit()));
    delBtn.addEventListener('click', async () => {
      await runAction(delBtn, () => api.del(`/ratecard/${encodeURIComponent(item.id)}`), { busyLabel: 'Deleting…', errSlot: err });
      reload();
    });
    mount(row, h('div', {}, [
      h('div', { class: 'rate-row-main' }, [
        h('span', { class: 'rate-name', text: item.name }),
        h('span', { class: 'rate-price', text: `${moneyFromPounds(item.unitPrice)} / ${item.unit}` }),
      ]),
      buttonRow([editBtn, delBtn]),
      err,
    ]));
  };

  const renderEdit = () => {
    const nameInput = makeInput({ value: item.name });
    const unitInput = makeInput({ value: item.unit });
    const priceInput = makeInput({ type: 'number', value: item.unitPrice, inputmode: 'decimal', step: 'any', min: '0' });
    const kindSelect = makeSelect(RATE_CARD_KINDS.map((k) => ({ value: k, label: k })), { value: item.kind });
    const err = errorSlot();
    const save = button('Save', { variant: 'primary', small: true });
    const cancel = button('Cancel', { small: true });
    cancel.addEventListener('click', renderRead);
    save.addEventListener('click', async () => {
      const name = nameInput.value.trim();
      const unit = unitInput.value.trim();
      if (!name || !unit) return setError(err, 'Name and unit are required.');
      const payload = { kind: kindSelect.value, name, unit, unitPrice: toNum(priceInput.value) };
      const updated = await runAction(save, () => api.put(`/ratecard/${encodeURIComponent(item.id)}`, payload), { busyLabel: 'Saving…', errSlot: err });
      if (updated) reload();
    });
    return h('div', { class: 'rate-edit' }, [
      field('Kind', kindSelect),
      field('Name', nameInput),
      field('Unit', unitInput),
      field('Unit price (£)', priceInput),
      err,
      buttonRow([save, cancel]),
    ]);
  };

  renderRead();
  return row;
}

// Add-a-new-rate-card-item form.
function addItemForm(reload) {
  const kindSelect = makeSelect(RATE_CARD_KINDS.map((k) => ({ value: k, label: k })), { value: 'labour' });
  const nameInput = makeInput({ placeholder: 'e.g. Standard labour' });
  const unitInput = makeInput({ placeholder: 'e.g. hour, each, m' });
  const priceInput = makeInput({ type: 'number', placeholder: '0.00', inputmode: 'decimal', step: 'any', min: '0' });
  const err = errorSlot();
  const add = button('Add item', { variant: 'primary' });
  add.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    const unit = unitInput.value.trim();
    if (!name || !unit) return setError(err, 'Name and unit are required.');
    const payload = { kind: kindSelect.value, name, unit, unitPrice: toNum(priceInput.value) };
    const created = await runAction(add, () => api.post('/ratecard', payload), { busyLabel: 'Adding…', errSlot: err });
    if (created) reload();
  });

  return card([
    field('Kind', kindSelect),
    field('Name', nameInput),
    field('Unit', unitInput),
    field('Unit price (£)', priceInput),
    err,
    buttonRow([add]),
  ], { title: 'Add item' });
}
